/*
 * Design and functionality adapted from shadcn/ui sidebar component.
 * shadcn/ui — MIT License — Copyright (c) 2023 shadcn
 * https://ui.shadcn.com/docs/components/sidebar
 */

import { settings } from "../stores/settings.js";
import { sidebar } from "../stores/ui.js";
import { AppAvatar } from "./app-avatar.js";
import { AppButton } from "./app-button.js";
import {
  AppSidebarContent,
  AppSidebarFooter,
  AppSidebarGroup,
  AppSidebarGroupContent,
  AppSidebarGroupLabel,
  AppSidebarHeader,
  AppSidebarMenu,
  AppSidebarMenuAction,
  AppSidebarMenuButton,
  AppSidebarMenuItem,
  AppSidebarPanel,
  AppSidebarRail,
} from "./app-sidebar-primitives.js";
import { IconArchive } from "./icons/archive.js";
import { IconMessagesSquare } from "./icons/messages-square.js";
import { IconChevronDown } from "./icons/chevron-down.js";
import { IconChevronRight } from "./icons/chevron-right.js";
import { IconChevronsUpDown } from "./icons/chevrons-up-down.js";
import { IconFolder } from "./icons/folder.js";
import { IconPanelLeftClose } from "./icons/panel-left-close.js";
import { IconPanelLeftOpen } from "./icons/panel-left-open.js";
import { IconPlus } from "./icons/plus.js";
import { IconSettingsHorizontal } from "./icons/settings-horizontal.js";
import { IconClock } from "./icons/clock.js";
import { IconStrideMark } from "./icons/stride-mark.js";

const styles = css`
  :host {
    --sidebar-width: 260px;
    --sidebar-width-icon: 48px;
    display: block;
    height: 100%;
    width: fit-content;
  }
  app-sidebar-panel {
    --sidebar-width: 260px;
    --sidebar-width-icon: 48px;
  }
  .scrim {
    border: 0;
    display: none;
    padding: 0;
  }
  .brand {
    align-items: center;
    display: flex;
    gap: 8px;
    min-width: 0;
  }
  .mark {
    align-items: center;
    background: var(--primary);
    border-radius: 8px;
    color: var(--primary-foreground);
    display: inline-flex;
    flex: 0 0 auto;
    height: 32px;
    justify-content: center;
    width: 32px;
  }
  .brand strong {
    color: var(--foreground);
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    min-width: 0;
  }
  app-sidebar-panel[state="collapsed"] .mark,
  app-sidebar-panel[state="collapsed"] .brand strong,
  app-sidebar-panel[state="collapsed"] .groups {
    display: none;
  }
  .icon {
    align-items: center;
    display: inline-flex;
    flex: 0 0 16px;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
  .icon > * {
    height: 16px;
    width: 16px;
  }
  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .nav-action-item {
    box-sizing: border-box;
    list-style: none;
    padding: 0 8px;
    width: 100%;
  }
  .nav-action {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    box-sizing: border-box;
    color: var(--sidebar-fg, var(--foreground));
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 14px;
    gap: 8px;
    height: 32px;
    outline: none;
    overflow: hidden;
    padding: 0 8px;
    text-align: left;
    white-space: nowrap;
    width: 100%;
  }
  .nav-action:hover,
  .nav-action:focus-visible {
    background: var(--sidebar-accent, var(--accent));
  }
  .nav-action:focus-visible {
    box-shadow: 0 0 0 2px var(--ring-shadow, rgb(24 24 27 / 12%));
  }
  app-sidebar-panel[state="collapsed"] .nav-action {
    width: 32px;
  }
  app-sidebar-panel[state="collapsed"] .nav-action .label {
    display: none;
  }
  .groups {
    width: 100%;
  }
  .group-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chevron {
    align-items: center;
    color: var(--muted-foreground);
    display: inline-flex;
    flex: 0 0 16px;
    height: 16px;
    justify-content: center;
    opacity: 0;
    transition: opacity 140ms ease;
    width: 16px;
  }
  app-sidebar-group-label:hover .chevron,
  app-sidebar-group-label:focus-within .chevron {
    opacity: 1;
  }
  .chevron > *,
  .chevron > * > * {
    align-items: center;
    display: inline-flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
  .chevron .closed-mark,
  app-sidebar-group.closed .chevron .open-mark {
    display: none;
  }
  app-sidebar-group.closed .chevron .closed-mark {
    display: inline-flex;
  }
  .thread-label {
    align-items: center;
    display: flex;
    gap: 7px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .run-pulse {
    animation: run-pulse 1.6s ease-out infinite;
    background: var(--primary);
    border-radius: 999px;
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary) 35%, transparent);
    display: inline-block;
    flex: 0 0 6px;
    height: 6px;
    width: 6px;
  }
  .thread-menu {
    background: var(--sidebar-bg, var(--secondary));
    display: none;
    position: absolute;
    right: 4px;
    top: 3px;
  }
  app-sidebar-menu-item:hover .thread-menu,
  .thread-menu[aria-expanded="true"] {
    display: inline-flex;
  }
  @keyframes run-pulse {
    70% {
      box-shadow: 0 0 0 5px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }
  @media (max-width: 767px) {
    :host([hydrated]) .scrim[data-open="true"] {
      background: rgb(0 0 0 / 36%);
      display: block;
      inset: 0;
      position: fixed;
      z-index: 40;
    }
    .icon {
      flex-basis: 20px;
      height: 20px;
      width: 20px;
    }
    .icon > * {
      height: 20px;
      width: 20px;
    }
    .thread-menu {
      display: inline-flex;
      top: 8px;
    }
    .nav-action {
      font-size: 16px;
      height: 44px;
      padding: 0 12px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .chevron {
      transition: none;
    }
    .run-pulse {
      animation: none;
    }
  }
`;

const accountFooterStyles = css`
  .account-footer {
    display: block;
    position: relative;
    width: 100%;
  }

  .trigger {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--sidebar-fg, var(--foreground));
    cursor: pointer;
    display: flex;
    font: inherit;
    gap: 10px;
    min-height: 48px;
    outline: none;
    padding: 8px;
    text-align: left;
    width: 100%;
  }

  .trigger:hover,
  .trigger:focus-visible,
  .trigger[aria-expanded="true"] {
    background: var(--sidebar-accent, var(--accent));
  }

  .trigger:focus-visible {
    box-shadow: 0 0 0 2px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  .identity {
    display: grid;
    flex: 1;
    line-height: 1.25;
    min-width: 0;
  }

  .name,
  .username {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
  }

  .username {
    color: var(--muted-foreground);
    font-size: 12px;
  }

  .selector {
    align-items: center;
    color: var(--muted-foreground);
    display: inline-flex;
    justify-content: center;
    margin-left: auto;
  }

  .menu {
    background: var(--popover, var(--background));
    border: 1px solid var(--border);
    border-radius: 8px;
    bottom: calc(100% + 4px);
    box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
    box-sizing: border-box;
    display: grid;
    gap: 2px;
    left: 0;
    padding: 4px;
    position: absolute;
    right: 0;
    z-index: 80;
  }

  .menu[hidden] {
    display: none;
  }

  .menu a,
  .menu button {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    box-sizing: border-box;
    color: var(--popover-foreground, var(--foreground));
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 14px;
    gap: 8px;
    height: 34px;
    outline: none;
    padding: 0 8px;
    text-align: left;
    text-decoration: none;
    width: 100%;
  }

  .menu a:hover,
  .menu a:focus-visible,
  .menu button:hover,
  .menu button:focus-visible {
    background: var(--accent);
  }

  .menu .icon,
  .menu .icon > * {
    align-items: center;
    display: inline-flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }

  app-sidebar-panel[state="collapsed"] .account-footer .trigger {
    height: 32px;
    min-height: 32px;
    padding: 0;
    width: 32px;
  }

  app-sidebar-panel[state="collapsed"] .account-footer .identity,
  app-sidebar-panel[state="collapsed"] .account-footer .selector {
    display: none;
  }

  @media (max-width: 767px) {
    .trigger {
      min-height: 52px;
    }

    .menu a,
    .menu button {
      height: 42px;
      font-size: 16px;
    }
  }
`;

const toggleStyles = css`
  :host {
    display: inline-flex;
  }
  icon-panel-left-open,
  icon-panel-left-close {
    height: 16px;
    width: 16px;
  }
  .brand-mark {
    align-items: center;
    background: var(--primary);
    border-radius: 8px;
    color: var(--primary-foreground);
    display: inline-flex;
    height: 32px;
    justify-content: center;
    width: 32px;
  }
  .hover-icon {
    display: none;
  }
  .with-brand:hover .brand-mark,
  .with-brand:focus-within .brand-mark {
    display: none;
  }
  .with-brand:hover .hover-icon,
  .with-brand:focus-within .hover-icon {
    align-items: center;
    display: inline-flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
`;

const navigationItemStyles = css`
  :host {
    display: block;
    width: 100%;
  }
  .icon {
    align-items: center;
    display: inline-flex;
    flex: 0 0 16px;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
  .icon > * {
    height: 16px;
    width: 16px;
  }
  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: 767px) {
    .icon {
      flex-basis: 20px;
      height: 20px;
      width: 20px;
    }
    .icon > * {
      height: 20px;
      width: 20px;
    }
  }
`;

const threadGroupStyles = css`
  :host {
    display: block;
    width: 100%;
  }
  .group-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chevron {
    align-items: center;
    color: var(--muted-foreground);
    display: inline-flex;
    flex: 0 0 16px;
    height: 16px;
    justify-content: center;
    opacity: 0;
    transition: opacity 140ms ease;
    width: 16px;
  }
  app-sidebar-group-label:hover .chevron,
  app-sidebar-group-label:focus-within .chevron {
    opacity: 1;
  }
  .chevron > *,
  .chevron > * > * {
    align-items: center;
    display: inline-flex;
    height: 16px;
    justify-content: center;
    width: 16px;
  }
  .chevron .closed-mark,
  app-sidebar-group.closed .chevron .open-mark {
    display: none;
  }
  app-sidebar-group.closed .chevron .closed-mark {
    display: inline-flex;
  }
  .thread-label {
    align-items: center;
    display: flex;
    gap: 7px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .run-pulse {
    animation: run-pulse 1.6s ease-out infinite;
    background: var(--primary);
    border-radius: 999px;
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary) 35%, transparent);
    display: inline-block;
    flex: 0 0 6px;
    height: 6px;
    width: 6px;
  }
  .thread-menu-wrap {
    display: none;
    position: absolute;
    right: 4px;
    top: 3px;
  }
  app-sidebar-menu-item:hover .thread-menu-wrap,
  .thread-menu-wrap:focus-within {
    display: inline-flex;
  }
  @keyframes run-pulse {
    70% {
      box-shadow: 0 0 0 5px transparent;
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }
  @media (max-width: 767px) {
    .thread-menu-wrap {
      display: inline-flex;
      top: 8px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .chevron {
      transition: none;
    }
    .run-pulse {
      animation: none;
    }
  }
`;

export interface SidebarThread {
  id: string;
  title: string;
}
export interface SidebarProject {
  id: string;
  title: string;
  threads: SidebarThread[];
}

interface SidebarHost extends HTMLElement {
  projects: SidebarProject[];
  threads: SidebarThread[];
}

interface UserEvent {
  id: string;
  kind:
    | {
        type: "thread_created";
        thread_id: string;
        title: string;
        project_id: string | null;
      }
    | { type: "thread_renamed"; thread_id: string; title: string }
    | { type: "thread_archived" | "thread_deleted"; thread_id: string }
    | { type: "thread_restored" | "resync"; thread_id?: string }
    | { type: "thread_run_status"; thread_id: string; running: boolean }
    | {
        type: "notification";
        notification_id: string;
        title: string;
        message: string;
        thread_id: string | null;
      };
}

function removeThread(host: SidebarHost, threadId: string): void {
  host.threads = host.threads.filter((thread) => thread.id !== threadId);
  host.projects = host.projects.map((project) => ({
    ...project,
    threads: project.threads.filter((thread) => thread.id !== threadId),
  }));
}

function applyUserEvent(host: SidebarHost, event: UserEvent): void {
  const kind = event.kind;
  if (kind.type === "thread_created") {
    removeThread(host, kind.thread_id);
    const thread = { id: kind.thread_id, title: kind.title };
    const project = kind.project_id
      ? host.projects.find((candidate) => candidate.id === kind.project_id)
      : undefined;
    if (project)
      host.projects = host.projects.map((candidate) =>
        candidate.id === project.id
          ? { ...candidate, threads: [thread, ...candidate.threads] }
          : candidate,
      );
    else host.threads = [thread, ...host.threads];
    return;
  }
  if (kind.type === "thread_renamed") {
    host.threads = host.threads.map((thread) =>
      thread.id === kind.thread_id ? { ...thread, title: kind.title } : thread,
    );
    host.projects = host.projects.map((project) => ({
      ...project,
      threads: project.threads.map((thread) =>
        thread.id === kind.thread_id
          ? { ...thread, title: kind.title }
          : thread,
      ),
    }));
    return;
  }
  if (kind.type === "thread_archived" || kind.type === "thread_deleted") {
    removeThread(host, kind.thread_id);
    return;
  }
  if (kind.type === "thread_run_status") {
    sidebar.runningThreads = kind.running
      ? [...new Set([...sidebar.runningThreads, kind.thread_id])]
      : sidebar.runningThreads.filter((id) => id !== kind.thread_id);
    return;
  }
  if (kind.type !== "notification") void resyncThreads(host);
}

async function resyncThreads(host: SidebarHost): Promise<void> {
  const response = await fetch("/api/threads");
  if (!response.ok) return;
  const threads = (await response.json()) as {
    id: string;
    title: string;
    project_id: string | null;
  }[];
  host.projects = host.projects.map((project) => ({
    ...project,
    threads: threads
      .filter((thread) => thread.project_id === project.id)
      .map(({ id, title }) => ({ id, title })),
  }));
  const projectIds = new Set(host.projects.map((project) => project.id));
  host.threads = threads
    .filter(
      (thread) => !thread.project_id || !projectIds.has(thread.project_id),
    )
    .map(({ id, title }) => ({ id, title }));
}

const MOBILE_QUERY = "(max-width: 767px)";
const SIDEBAR_STATE_COOKIE = "stride_sidebar_state";

function storedDesktopSidebarStatus(): "open" | "collapsed" {
  try {
    const value = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${SIDEBAR_STATE_COOKIE}=`))
      ?.slice(SIDEBAR_STATE_COOKIE.length + 1);
    return value === "collapsed" ? "collapsed" : "open";
  } catch {
    return "open";
  }
}

function setDesktopSidebarStatus(status: "open" | "collapsed"): void {
  sidebar.status = status;
  try {
    document.cookie = `${SIDEBAR_STATE_COOKIE}=${status}; Path=/; Max-Age=31536000; SameSite=Lax`;
  } catch {}
}

function toggleSidebar(): void {
  if (window.matchMedia(MOBILE_QUERY).matches)
    sidebar.status = sidebar.status === "open" ? "hidden" : "open";
  else
    setDesktopSidebarStatus(
      sidebar.status === "collapsed" ? "open" : "collapsed",
    );
}

export class SidebarNavigationItem extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof SidebarNavigationItem)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["href","data-href","label","data-label","kind","data-kind","active","data-active","collapsed","data-collapsed"];

  #href;
  #label;
  #kind;
  #active = false;
  #collapsed = false;
  #hrefSet = false;
  #labelSet = false;
  #kindSet = false;
  #activeSet = false;
  #collapsedSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get href() {
    return this.#href;
  }

  set href(value) {
    const raw = value;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#href, next)) return;
    this.#href = next;
    this.#hrefSet = true;
    this.#updateBindings('href');
  }

  get label() {
    return this.#label;
  }

  set label(value) {
    const raw = value;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#label, next)) return;
    this.#label = next;
    this.#labelSet = true;
    this.#updateBindings('label');
  }

  get kind() {
    return this.#kind;
  }

  set kind(value) {
    const raw = value;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#kind, next)) return;
    this.#kind = next;
    this.#kindSet = true;
    this.#updateBindings('kind');
  }

  get active() {
    return this.#active;
  }

  set active(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#active, next)) return;
    this.#active = next;
    this.#activeSet = true;
    this.#updateBindings('active');
  }

  get collapsed() {
    return this.#collapsed;
  }

  set collapsed(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#collapsed, next)) return;
    this.#collapsed = next;
    this.#collapsedSet = true;
    this.#updateBindings('collapsed');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-href')) this.href = this.dataset.href;
    if (this.hasAttribute('data-label')) this.label = this.dataset.label;
    if (this.hasAttribute('data-kind')) this.kind = this.dataset.kind;
    if (this.hasAttribute('data-active')) this.active = this.dataset.active;
    if (this.hasAttribute('data-collapsed')) this.collapsed = this.dataset.collapsed;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.href = props.href;
      this.label = props.label;
      this.kind = props.kind;
      this.active = props.active;
      this.collapsed = props.collapsed;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#hrefSet) this.href = this.dataset.href;
    if (!this.#labelSet) this.label = this.dataset.label;
    if (!this.#kindSet) this.kind = this.dataset.kind;
    if (!this.#activeSet) this.active = this.dataset.active;
    if (!this.#collapsedSet) this.collapsed = this.dataset.collapsed;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'href':
      case 'data-href':
        this.href = newValue;
        break;
      case 'label':
      case 'data-label':
        this.label = newValue;
        break;
      case 'kind':
      case 'data-kind':
        this.kind = newValue;
        break;
      case 'active':
      case 'data-active':
        this.active = newValue;
        break;
      case 'collapsed':
      case 'data-collapsed':
        this.collapsed = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(navigationItemStyles)}</style><app-sidebar-menu-item><!--argon-text:0-->${""}<!--/argon-text:0--><app-sidebar-menu-button data-href="${this.#attrEsc(this.#href)}" data-active="${this.#attrEsc(this.#active)}" data-collapsed="${this.#attrEsc(this.#collapsed)}" data-argon-bind="0"><!--argon-text:1-->${""}<!--/argon-text:1--><span class="icon"><!--argon-text:2-->${(this.#kind === "tasks") ? `<icon-messages-square>${""}</icon-messages-square>` : (this.#kind === "files") ? `<icon-folder>${""}</icon-folder>` : (this.#kind === "automations") ? `<icon-clock>${""}</icon-clock>` : `<icon-archive>${""}</icon-archive>`}<!--/argon-text:2--></span><span class="label"><!--argon-text:3-->${this.#escText(this.#label)}<!--/argon-text:3--></span></app-sidebar-menu-button></app-sidebar-menu-item>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'href' || prop === 'active' || prop === 'collapsed') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'kind') {
      const __argonNext = `${(this.#kind === "tasks") ? `<icon-messages-square>${""}</icon-messages-square>` : (this.#kind === "files") ? `<icon-folder>${""}</icon-folder>` : (this.#kind === "automations") ? `<icon-clock>${""}</icon-clock>` : `<icon-archive>${""}</icon-archive>`}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'label') {
      const __argonNext = `${this.#escText(this.#label)}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'href') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#href)}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-href', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'active') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#active)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-active', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'collapsed') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#collapsed)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('data-collapsed', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}

function profileInitials(fullName: string, username: string): string {
  const source = fullName.trim() || username.trim();
  if (source.length === 0) return "?";
  return source.slice(0, 1).toUpperCase();
}

export class SidebarThreadGroup extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof SidebarThreadGroup)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["title","data-title","threads","data-threads","projectid","data-project-id","projecttitle","data-project-title"];

  #title;
  #threads = [];
  #projectId = "";
  #projectTitle = "";
  #closed;
  #titleSet = false;
  #threadsSet = false;
  #projectIdSet = false;
  #projectTitleSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #eventContexts = {};
  #storeCleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value;
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  get threads() {
    return this.#threads;
  }

  set threads(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#threads, next)) return;
    this.#threads = next;
    this.#threadsSet = true;
    this.#updateBindings('threads');
  }

  get projectId() {
    return this.#projectId;
  }

  set projectId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#projectId, next)) return;
    this.#projectId = next;
    this.#projectIdSet = true;
    this.#updateBindings('projectId');
  }

  get projectTitle() {
    return this.#projectTitle;
  }

  set projectTitle(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#projectTitle, next)) return;
    this.#projectTitle = next;
    this.#projectTitleSet = true;
    this.#updateBindings('projectTitle');
  }

  #setClosed(value) {
    if (Object.is(this.#closed, value)) return;
    this.#closed = value;
    this.#updateBindings('closed');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
    if (this.hasAttribute('data-threads')) this.threads = this.dataset.threads;
    if (this.hasAttribute('data-project-id')) this.projectId = this.dataset.projectId;
    if (this.hasAttribute('data-project-title')) this.projectTitle = this.dataset.projectTitle;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.title = props.title;
      this.threads = props.threads;
      this.projectId = props.projectId;
      this.projectTitle = props.projectTitle;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#titleSet) this.title = this.dataset.title;
    if (!this.#threadsSet) this.threads = this.dataset.threads;
    if (!this.#projectIdSet) this.projectId = this.dataset.projectId;
    if (!this.#projectTitleSet) this.projectTitle = this.dataset.projectTitle;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
  }

  disconnectedCallback() {
    this.#disconnectStores();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
      case 'threads':
      case 'data-threads':
        this.threads = newValue;
        break;
      case 'projectid':
      case 'data-project-id':
        this.projectId = newValue;
        break;
      case 'projecttitle':
      case 'data-project-title':
        this.projectTitle = newValue;
        break;
    }
  }

  #initState() {
    this.#closed = false;
  }

  #render() {
    
    return `<style>${this.#escText(threadGroupStyles)}</style><app-sidebar-group class="${this.#escAttr(this.#closed ? "closed" : "")}" data-argon-bind="0"><!--argon-text:0-->${""}<!--/argon-text:0--><app-sidebar-group-label data-argon="17699"><!--argon-text:1-->${""}<!--/argon-text:1--><span class="group-title"><!--argon-text:2-->${this.#escText(this.#title)}<!--/argon-text:2--></span><span class="chevron" aria-hidden="true"><span class="open-mark"><icon-chevron-down><!--argon-text:3-->${""}<!--/argon-text:3--></icon-chevron-down></span><span class="closed-mark"><icon-chevron-right><!--argon-text:4-->${""}<!--/argon-text:4--></icon-chevron-right></span></span><!--argon-text:5-->${(this.#projectId !== "") ? `<app-sidebar-menu-action slot="actions" small title="New thread" data-argon="18231">${""}+</app-sidebar-menu-action><app-sidebar-menu-action slot="actions" small title="Rename" data-argon="18624">${""}✎</app-sidebar-menu-action><app-sidebar-menu-action slot="actions" small title="Delete" data-argon="19013">${""}✕</app-sidebar-menu-action>` : ""}<!--/argon-text:5--></app-sidebar-group-label><app-sidebar-group-content${this.#closed ? ' hidden' : ''} data-argon-bind="1"><!--argon-text:6-->${""}<!--/argon-text:6--><app-sidebar-menu><!--argon-text:7-->${""}${this.#threads.map((thread, __argonIndex) => this.#mapEventItem(`<app-sidebar-menu-item>${""}<app-sidebar-menu-button compact data-href="${this.#attrEsc(`/threads/${thread.id}`)}" data-active="${this.#attrEsc(thread.id === sidebar.activeThread)}">${""}<span class="thread-label">${sidebar.runningThreads.includes(thread.id) ? "<span class=\"run-pulse\" title=\"Running\"></span>" : ""}${this.#escText(thread.title)}</span></app-sidebar-menu-button><span class="thread-menu-wrap"><app-sidebar-menu-action title="Thread actions" aria-label="Thread actions" data-argon="20109">${""}⋯</app-sidebar-menu-action></span></app-sidebar-menu-item>`, "initial:11:20109:" + __argonIndex, { 20109: (event: Event) => this.dispatchEvent(new CustomEvent("thread-menu", { bubbles: true, composed: true, detail: {
        id: thread.id,
        title: thread.title,
        anchor: event.currentTarget as HTMLElement,
    } })) }, ["20109"])).join("")}<!--/argon-text:7--></app-sidebar-menu></app-sidebar-group-content></app-sidebar-group>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("sidebar.activeThread", () => this.#updateBindings("store:../stores/ui.js:sidebar.activeThread")));
    this.#storeCleanups.push(__argonStoreSubscribe0("sidebar.runningThreads", () => this.#updateBindings("store:../stores/ui.js:sidebar.runningThreads")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'title') {
      const __argonNext = `${this.#escText(this.#title)}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined || prop === 'projectId') {
      const __argonNext = `${(this.#projectId !== "") ? `<app-sidebar-menu-action slot="actions" small title="New thread" data-argon="18231">${""}+</app-sidebar-menu-action><app-sidebar-menu-action slot="actions" small title="Rename" data-argon="18624">${""}✎</app-sidebar-menu-action><app-sidebar-menu-action slot="actions" small title="Delete" data-argon="19013">${""}✕</app-sidebar-menu-action>` : ""}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'threads' || prop === 'store:../stores/ui.js:sidebar.activeThread' || prop === 'store:../stores/ui.js:sidebar.runningThreads') {
      const __argonNext = `${""}${this.#threads.map((thread, __argonIndex) => this.#mapEventItem(`<app-sidebar-menu-item>${""}<app-sidebar-menu-button compact data-href="${this.#attrEsc(`/threads/${thread.id}`)}" data-active="${this.#attrEsc(thread.id === sidebar.activeThread)}">${""}<span class="thread-label">${sidebar.runningThreads.includes(thread.id) ? "<span class=\"run-pulse\" title=\"Running\"></span>" : ""}${this.#escText(thread.title)}</span></app-sidebar-menu-button><span class="thread-menu-wrap"><app-sidebar-menu-action title="Thread actions" aria-label="Thread actions" data-argon="20109">${""}⋯</app-sidebar-menu-action></span></app-sidebar-menu-item>`, "7:11:20109:" + __argonIndex, { 20109: (event: Event) => this.dispatchEvent(new CustomEvent("thread-menu", { bubbles: true, composed: true, detail: {
        id: thread.id,
        title: thread.title,
        anchor: event.currentTarget as HTMLElement,
    } })) }, ["20109"])).join("")}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'closed') && this.#attr0) {
      const __argonNext = `${this.#closed ? "closed" : ""}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'closed') && this.#attr1) {
      this.#attr1.toggleAttribute('hidden', !!(this.#closed));
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '17699').forEach(el => this.#listen(el, '17699', 'toggle', () => (this.#setClosed(!this.#closed))));
    this.#eventTargets(scope, '18231').forEach(el => this.#listen(el, '18231', 'select', () => this.dispatchEvent(new CustomEvent("project-new-thread", { bubbles: true, composed: true, detail: {
        id: this.#projectId,
        title: this.#projectTitle,
    } }))));
    this.#eventTargets(scope, '18624').forEach(el => this.#listen(el, '18624', 'select', () => this.dispatchEvent(new CustomEvent("project-rename", { bubbles: true, composed: true, detail: {
        id: this.#projectId,
        title: this.#projectTitle,
    } }))));
    this.#eventTargets(scope, '19013').forEach(el => this.#listen(el, '19013', 'select', () => this.dispatchEvent(new CustomEvent("project-delete", { bubbles: true, composed: true, detail: { id: this.#projectId } }))));
    this.#eventTargets(scope, '20109').forEach(el => this.#listen(el, '20109', 'select', (event: Event) => this.dispatchEvent(new CustomEvent("thread-menu", { bubbles: true, composed: true, detail: {
        id: thread.id,
        title: thread.title,
        anchor: event.currentTarget as HTMLElement,
    } }))));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}

export class AppSidebar extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSidebar)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["projects","data-projects","threads","data-threads","username","data-username","fullname","data-full-name"];

  #projects = [];
  #threads = [];
  #username = "";
  #fullName = "";
  #accountOpen;
  #projectsSet = false;
  #threadsSet = false;
  #usernameSet = false;
  #fullNameSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #textStart9;
  #textEnd9;
  #textLast9;
  #textStart10;
  #textEnd10;
  #textLast10;
  #textStart11;
  #textEnd11;
  #textLast11;
  #textStart12;
  #textEnd12;
  #textLast12;
  #textStart13;
  #textEnd13;
  #textLast13;
  #textStart14;
  #textEnd14;
  #textLast14;
  #textStart15;
  #textEnd15;
  #textLast15;
  #textStart16;
  #textEnd16;
  #textLast16;
  #textStart17;
  #textEnd17;
  #textLast17;
  #textStart18;
  #textEnd18;
  #textLast18;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attr4;
  #attr5;
  #attr6;
  #attr7;
  #attr8;
  #attr9;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #attrLast6;
  #attrLast7;
  #attrLast8;
  #attrLast9;
  #attrLast10;
  #attrLast11;
  #attrLast12;
  #storeCleanups = [];
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get projects() {
    return this.#projects;
  }

  set projects(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#projects, next)) return;
    this.#projects = next;
    this.#projectsSet = true;
    this.#updateBindings('projects');
  }

  get threads() {
    return this.#threads;
  }

  set threads(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#threads, next)) return;
    this.#threads = next;
    this.#threadsSet = true;
    this.#updateBindings('threads');
  }

  get username() {
    return this.#username;
  }

  set username(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#username, next)) return;
    this.#username = next;
    this.#usernameSet = true;
    this.#updateBindings('username');
  }

  get fullName() {
    return this.#fullName;
  }

  set fullName(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#fullName, next)) return;
    this.#fullName = next;
    this.#fullNameSet = true;
    this.#updateBindings('fullName');
  }

  #setAccountOpen(value) {
    if (Object.is(this.#accountOpen, value)) return;
    this.#accountOpen = value;
    this.#updateBindings('accountOpen');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-projects')) this.projects = this.dataset.projects;
    if (this.hasAttribute('data-threads')) this.threads = this.dataset.threads;
    if (this.hasAttribute('data-username')) this.username = this.dataset.username;
    if (this.hasAttribute('data-full-name')) this.fullName = this.dataset.fullName;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.projects = props.projects;
      this.threads = props.threads;
      this.username = props.username;
      this.fullName = props.fullName;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#projectsSet) this.projects = this.dataset.projects;
    if (!this.#threadsSet) this.threads = this.dataset.threads;
    if (!this.#usernameSet) this.username = this.dataset.username;
    if (!this.#fullNameSet) this.fullName = this.dataset.fullName;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const sync = () => {
        sidebar.status = mq.matches ? "hidden" : storedDesktopSidebarStatus();
    };
    sync();
    mq.addEventListener("change", sync);
    const host = this as SidebarHost;
    let socket: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    const closeAccount = (event: Event) => {
        const footer = this.shadowRoot?.querySelector(".account-footer");
        if (this.#accountOpen && footer && !event.composedPath().includes(footer))
            this.#setAccountOpen(false);
    };
    document.addEventListener("click", closeAccount);
    const connect = () => {
        if (retry) {
            clearTimeout(retry);
            retry = null;
        }
        const previous = socket;
        socket = null;
        previous?.close();
        const protocol = location.protocol === "https:" ? "wss:" : "ws:";
        const next = new WebSocket(`${protocol}//${location.host}/api/events`);
        socket = next;
        next.onopen = () => void resyncThreads(host);
        next.onmessage = (event) => applyUserEvent(host, JSON.parse(event.data as string) as UserEvent);
        next.onclose = () => {
            if (socket !== next)
                return;
            socket = null;
            if (!stopped)
                retry = setTimeout(connect, 2000 + Math.random() * 3000);
        };
    };
    const reconnectOnFocus = () => connect();
    window.addEventListener("focus", reconnectOnFocus);
    connect();
    return () => {
        stopped = true;
        mq.removeEventListener("change", sync);
        document.removeEventListener("click", closeAccount);
        window.removeEventListener("focus", reconnectOnFocus);
        if (retry)
            clearTimeout(retry);
        socket?.close();
    };
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#disconnectStores();
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'projects':
      case 'data-projects':
        this.projects = newValue;
        break;
      case 'threads':
      case 'data-threads':
        this.threads = newValue;
        break;
      case 'username':
      case 'data-username':
        this.username = newValue;
        break;
      case 'fullname':
      case 'data-full-name':
        this.fullName = newValue;
        break;
    }
  }

  #initState() {
    this.#accountOpen = false;
  }

  #render() {
    const collapsed = sidebar.status === "collapsed";
    return `<style>${this.#escText(styles)}</style><style>${this.#escText(accountFooterStyles)}</style><app-sidebar-panel state="${this.#escAttr(sidebar.status)}" data-argon-bind="0"><!--argon-text:0-->${""}<!--/argon-text:0--><app-sidebar-header><!--argon-text:1-->${""}<!--/argon-text:1--><div class="brand"><span class="mark" aria-hidden="true"><icon-stride-mark><!--argon-text:2-->${""}<!--/argon-text:2--></icon-stride-mark></span><strong>S.T.R.I.D.E.</strong><app-sidebar-toggle data-brand="stride"><!--argon-text:3-->${""}<!--/argon-text:3--></app-sidebar-toggle></div></app-sidebar-header><app-sidebar-content state="${this.#escAttr(sidebar.status)}" data-argon-bind="1"><!--argon-text:4-->${""}<!--/argon-text:4--><app-sidebar-menu><!--argon-text:5-->${""}<!--/argon-text:5--><sidebar-navigation-item data-href="/threads" data-label="New task" data-kind="tasks" data-collapsed="${this.#attrEsc(collapsed)}" data-argon-bind="2"><!--argon-text:6-->${""}<!--/argon-text:6--></sidebar-navigation-item><sidebar-navigation-item data-href="/files" data-label="Files" data-kind="files" data-active="${this.#attrEsc(sidebar.activePage === "files")}" data-collapsed="${this.#attrEsc(collapsed)}" data-argon-bind="3"><!--argon-text:7-->${""}<!--/argon-text:7--></sidebar-navigation-item><sidebar-navigation-item data-href="/automations" data-label="Automations" data-kind="automations" data-active="${this.#attrEsc(sidebar.activePage === "automations")}" data-collapsed="${this.#attrEsc(collapsed)}" data-argon-bind="4"><!--argon-text:8-->${""}<!--/argon-text:8--></sidebar-navigation-item><li class="nav-action-item"><button class="nav-action" type="button" data-argon="23972"><span class="icon"><icon-plus><!--argon-text:9-->${""}<!--/argon-text:9--></icon-plus></span><span class="label">New project</span></button></li></app-sidebar-menu><div class="groups"><!--argon-text:10-->${this.#projects.map((project) => `<sidebar-thread-group data-title="${this.#attrEsc(project.title)}" data-threads="${this.#attrEsc(project.threads)}" data-project-id="${this.#attrEsc(project.id)}" data-project-title="${this.#attrEsc(project.title)}">${""}</sidebar-thread-group>`).join("")}${(this.#threads.length > 0) ? `<sidebar-thread-group data-title="Threads" data-threads="${this.#attrEsc(this.#threads)}">${""}</sidebar-thread-group>` : ""}<!--/argon-text:10--></div></app-sidebar-content><app-sidebar-footer><!--argon-text:11-->${""}<!--/argon-text:11--><div class="account-footer"><button class="trigger" type="button" aria-label="Account menu" aria-haspopup="menu" aria-expanded="${this.#escAttr(this.#accountOpen ? "true" : "false")}" title="${this.#escAttr(collapsed ? (settings.fullName || this.#fullName) : "Account menu")}" data-argon="25075" data-argon-bind="5"><app-avatar data-fallback="${this.#attrEsc(profileInitials(settings.fullName || this.#fullName, settings.username || this.#username))}" data-argon-bind="6"><!--argon-text:12-->${""}<!--/argon-text:12--></app-avatar><span class="identity"><span class="name"><!--argon-text:13-->${this.#escText(settings.fullName || this.#fullName)}<!--/argon-text:13--></span><span class="username"><!--argon-text:14-->@${this.#escText(settings.username || this.#username)}<!--/argon-text:14--></span></span><span class="selector" aria-hidden="true"><icon-chevrons-up-down><!--argon-text:15-->${""}<!--/argon-text:15--></icon-chevrons-up-down></span></button><div class="menu" role="menu"${(!this.#accountOpen) ? ' hidden' : ''} data-argon-bind="7"><a href="/archived" role="menuitem" data-argon="25908"><span class="icon"><icon-archive><!--argon-text:16-->${""}<!--/argon-text:16--></icon-archive></span>Archived</a><button type="button" role="menuitem" data-argon="26149"><span class="icon"><icon-settings-horizontal><!--argon-text:17-->${""}<!--/argon-text:17--></icon-settings-horizontal></span>Settings</button><button type="button" role="menuitem" data-argon="26588"><span class="icon" aria-hidden="true">↪</span>Log out</button></div></div></app-sidebar-footer><app-sidebar-rail data-collapsed="${this.#attrEsc(collapsed)}" data-argon="26962" data-argon-bind="8"><!--argon-text:18-->${""}<!--/argon-text:18--></app-sidebar-rail></app-sidebar-panel><button class="scrim" type="button" aria-label="Close sidebar" data-open="${sidebar.status === "open"}" data-argon="27177" data-argon-bind="9"></button>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
        case 'argon-text:9':
          this.#textStart9 = marker;
          break;
        case '/argon-text:9':
          this.#textEnd9 = marker;
          break;
        case 'argon-text:10':
          this.#textStart10 = marker;
          break;
        case '/argon-text:10':
          this.#textEnd10 = marker;
          break;
        case 'argon-text:11':
          this.#textStart11 = marker;
          break;
        case '/argon-text:11':
          this.#textEnd11 = marker;
          break;
        case 'argon-text:12':
          this.#textStart12 = marker;
          break;
        case '/argon-text:12':
          this.#textEnd12 = marker;
          break;
        case 'argon-text:13':
          this.#textStart13 = marker;
          break;
        case '/argon-text:13':
          this.#textEnd13 = marker;
          break;
        case 'argon-text:14':
          this.#textStart14 = marker;
          break;
        case '/argon-text:14':
          this.#textEnd14 = marker;
          break;
        case 'argon-text:15':
          this.#textStart15 = marker;
          break;
        case '/argon-text:15':
          this.#textEnd15 = marker;
          break;
        case 'argon-text:16':
          this.#textStart16 = marker;
          break;
        case '/argon-text:16':
          this.#textEnd16 = marker;
          break;
        case 'argon-text:17':
          this.#textStart17 = marker;
          break;
        case '/argon-text:17':
          this.#textEnd17 = marker;
          break;
        case 'argon-text:18':
          this.#textStart18 = marker;
          break;
        case '/argon-text:18':
          this.#textEnd18 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#attr4 = root.querySelector('[data-argon-bind="4"]');
    this.#attr5 = root.querySelector('[data-argon-bind="5"]');
    this.#attr6 = root.querySelector('[data-argon-bind="6"]');
    this.#attr7 = root.querySelector('[data-argon-bind="7"]');
    this.#attr8 = root.querySelector('[data-argon-bind="8"]');
    this.#attr9 = root.querySelector('[data-argon-bind="9"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("sidebar.status", () => this.#updateBindings("store:../stores/ui.js:sidebar.status")));
    this.#storeCleanups.push(__argonStoreSubscribe0("sidebar.activePage", () => this.#updateBindings("store:../stores/ui.js:sidebar.activePage")));
    this.#storeCleanups.push(__argonStoreSubscribe1("settings.fullName", () => this.#updateBindings("store:../stores/settings.js:settings.fullName")));
    this.#storeCleanups.push(__argonStoreSubscribe1("settings.username", () => this.#updateBindings("store:../stores/settings.js:settings.username")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const collapsed = (prop === undefined || prop === 'store:../stores/ui.js:sidebar.status' || prop === 'store:../stores/ui.js:sidebar.activePage' || prop === 'store:../stores/settings.js:settings.fullName' || prop === 'fullName') ? (sidebar.status === "collapsed") : undefined;
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/ui.js:sidebar.activePage' || prop === 'store:../stores/ui.js:sidebar.status') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/ui.js:sidebar.activePage' || prop === 'store:../stores/ui.js:sidebar.status') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast9) {
        this.#textLast9 = __argonNext;
        this.#replaceRange(this.#textStart9, this.#textEnd9, __argonNext);
      }
    }
    if (prop === undefined || prop === 'projects' || prop === 'threads') {
      const __argonNext = `${this.#projects.map((project) => `<sidebar-thread-group data-title="${this.#attrEsc(project.title)}" data-threads="${this.#attrEsc(project.threads)}" data-project-id="${this.#attrEsc(project.id)}" data-project-title="${this.#attrEsc(project.title)}">${""}</sidebar-thread-group>`).join("")}${(this.#threads.length > 0) ? `<sidebar-thread-group data-title="Threads" data-threads="${this.#attrEsc(this.#threads)}">${""}</sidebar-thread-group>` : ""}`;
      if (__argonNext !== this.#textLast10) {
        this.#textLast10 = __argonNext;
        this.#replaceRange(this.#textStart10, this.#textEnd10, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast11) {
        this.#textLast11 = __argonNext;
        this.#replaceRange(this.#textStart11, this.#textEnd11, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/settings.js:settings.fullName' || prop === 'fullName' || prop === 'store:../stores/settings.js:settings.username' || prop === 'username') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast12) {
        this.#textLast12 = __argonNext;
        this.#replaceRange(this.#textStart12, this.#textEnd12, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/settings.js:settings.fullName' || prop === 'fullName') {
      const __argonNext = `${this.#escText(settings.fullName || this.#fullName)}`;
      if (__argonNext !== this.#textLast13) {
        this.#textLast13 = __argonNext;
        this.#replaceRange(this.#textStart13, this.#textEnd13, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/settings.js:settings.username' || prop === 'username') {
      const __argonNext = `@${this.#escText(settings.username || this.#username)}`;
      if (__argonNext !== this.#textLast14) {
        this.#textLast14 = __argonNext;
        this.#replaceRange(this.#textStart14, this.#textEnd14, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast15) {
        this.#textLast15 = __argonNext;
        this.#replaceRange(this.#textStart15, this.#textEnd15, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast16) {
        this.#textLast16 = __argonNext;
        this.#replaceRange(this.#textStart16, this.#textEnd16, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast17) {
        this.#textLast17 = __argonNext;
        this.#replaceRange(this.#textStart17, this.#textEnd17, __argonNext);
      }
    }
    if (prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast18) {
        this.#textLast18 = __argonNext;
        this.#replaceRange(this.#textStart18, this.#textEnd18, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr0) {
      const __argonNext = `${sidebar.status}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('state', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr1) {
      const __argonNext = `${sidebar.status}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('state', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr2) {
      const __argonNext = `${this.#attrRaw(collapsed)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr2.setAttribute('data-collapsed', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.activePage') && this.#attr3) {
      const __argonNext = `${this.#attrRaw(sidebar.activePage === "files")}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr3.setAttribute('data-active', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr3) {
      const __argonNext = `${this.#attrRaw(collapsed)}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr3.setAttribute('data-collapsed', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.activePage') && this.#attr4) {
      const __argonNext = `${this.#attrRaw(sidebar.activePage === "automations")}`;
      if (__argonNext !== this.#attrLast5) {
        this.#attrLast5 = __argonNext;
        this.#attr4.setAttribute('data-active', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr4) {
      const __argonNext = `${this.#attrRaw(collapsed)}`;
      if (__argonNext !== this.#attrLast6) {
        this.#attrLast6 = __argonNext;
        this.#attr4.setAttribute('data-collapsed', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'accountOpen') && this.#attr5) {
      const __argonNext = `${this.#accountOpen ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast7) {
        this.#attrLast7 = __argonNext;
        this.#attr5.setAttribute('aria-expanded', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status' || prop === 'store:../stores/settings.js:settings.fullName' || prop === 'fullName') && this.#attr5) {
      const __argonNext = `${collapsed ? (settings.fullName || this.#fullName) : "Account menu"}`;
      if (__argonNext !== this.#attrLast8) {
        this.#attrLast8 = __argonNext;
        this.#attr5.setAttribute('title', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/settings.js:settings.fullName' || prop === 'fullName' || prop === 'store:../stores/settings.js:settings.username' || prop === 'username') && this.#attr6) {
      const __argonNext = `${this.#attrRaw(profileInitials(settings.fullName || this.#fullName, settings.username || this.#username))}`;
      if (__argonNext !== this.#attrLast9) {
        this.#attrLast9 = __argonNext;
        this.#attr6.setAttribute('data-fallback', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'accountOpen') && this.#attr7) {
      this.#attr7.toggleAttribute('hidden', !!(!this.#accountOpen));
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr8) {
      const __argonNext = `${this.#attrRaw(collapsed)}`;
      if (__argonNext !== this.#attrLast11) {
        this.#attrLast11 = __argonNext;
        this.#attr8.setAttribute('data-collapsed', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr9) {
      const __argonNext = `${sidebar.status === "open"}`;
      if (__argonNext !== this.#attrLast12) {
        this.#attrLast12 = __argonNext;
        this.#attr9.setAttribute('data-open', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '23972').forEach(el => this.#listen(el, '23972', 'click', () => this.dispatchEvent(new CustomEvent("new-project", { bubbles: true, composed: true, detail: void 0 }))));
    this.#eventTargets(scope, '25075').forEach(el => this.#listen(el, '25075', 'click', (event: Event) => {
    event.stopPropagation();
    if (sidebar.status === "collapsed")
        setDesktopSidebarStatus("open");
    this.#setAccountOpen(!this.#accountOpen);
}));
    this.#eventTargets(scope, '25908').forEach(el => this.#listen(el, '25908', 'click', () => (this.#setAccountOpen(false))));
    this.#eventTargets(scope, '26149').forEach(el => this.#listen(el, '26149', 'click', () => {
    this.#setAccountOpen(false);
    settings.open = true;
    if (window.matchMedia(MOBILE_QUERY).matches)
        sidebar.status = "hidden";
}));
    this.#eventTargets(scope, '26588').forEach(el => this.#listen(el, '26588', 'click', () => {
    this.#setAccountOpen(false);
    this.dispatchEvent(new CustomEvent("logout", { bubbles: true, composed: true, detail: void 0 }));
}));
    this.#eventTargets(scope, '26962').forEach(el => this.#listen(el, '26962', 'toggle', () => toggleSidebar()));
    this.#eventTargets(scope, '27177').forEach(el => this.#listen(el, '27177', 'click', () => (sidebar.status = "hidden")));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}

export class AppSidebarToggle extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSidebarToggle)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["brand","data-brand"];

  #brand = "";
  #brandSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #storeCleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get brand() {
    return this.#brand;
  }

  set brand(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#brand, next)) return;
    this.#brand = next;
    this.#brandSet = true;
    this.#updateBindings('brand');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-brand')) this.brand = this.dataset.brand;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.brand = props.brand;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#brandSet) this.brand = this.dataset.brand;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
  }

  disconnectedCallback() {
    this.#disconnectStores();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'brand':
      case 'data-brand':
        this.brand = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const closed = sidebar.status !== "open";
    return `<style>${this.#escText(toggleStyles)}</style><app-button variant="ghost" size="${this.#escAttr((this.#brand !== "") ? "icon" : "icon-xs")}" title="${this.#escAttr(closed ? "Open sidebar" : "Close sidebar")}" class="${this.#escAttr((this.#brand !== "") ? "with-brand" : "")}" data-argon="27645" data-argon-bind="0"><!--argon-text:0-->${""}${((this.#brand !== "") && closed) ? `<span class="with-brand"><span class="brand-mark" aria-hidden="true"><icon-stride-mark>${""}</icon-stride-mark></span><span class="hover-icon"><icon-panel-left-open>${""}</icon-panel-left-open></span></span>` : closed ? `<icon-panel-left-open>${""}</icon-panel-left-open>` : `<icon-panel-left-close>${""}</icon-panel-left-close>`}<!--/argon-text:0--></app-button>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("sidebar.status", () => this.#updateBindings("store:../stores/ui.js:sidebar.status")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const closed = sidebar.status !== "open";
    if (prop === undefined || prop === 'brand' || prop === 'store:../stores/ui.js:sidebar.status') {
      const __argonNext = `${""}${((this.#brand !== "") && closed) ? `<span class="with-brand"><span class="brand-mark" aria-hidden="true"><icon-stride-mark>${""}</icon-stride-mark></span><span class="hover-icon"><icon-panel-left-open>${""}</icon-panel-left-open></span></span>` : closed ? `<icon-panel-left-open>${""}</icon-panel-left-open>` : `<icon-panel-left-close>${""}</icon-panel-left-close>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'brand') && this.#attr0) {
      const __argonNext = `${(this.#brand !== "") ? "icon" : "icon-xs"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('size', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/ui.js:sidebar.status') && this.#attr0) {
      const __argonNext = `${closed ? "Open sidebar" : "Close sidebar"}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('title', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'brand') && this.#attr0) {
      const __argonNext = `${(this.#brand !== "") ? "with-brand" : ""}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '27645').forEach(el => this.#listen(el, '27645', 'click', () => toggleSidebar()));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
